import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface Wallet {
  id: string;
  user_id: string;
  currency_id: number;
  currency: string;
  balance: string;
  locked: string;
  status: string;
  created_at: string;
  updated_at: string;
  wallet_type: {
    id: number;
    type: string;
    tenant_id: string;
    description: string;
    duration: number;
    is_active: boolean;
    is_unlimited: boolean;
    is_permanent: boolean;
    kind: 'CASH' | 'PROMO' | 'SYSTEM';
    payout_wallet_type_id?: number;
    sportsbook_enabled: boolean;
    casino_enabled: boolean;
    restrictions: [];
    created_at: string;
    updated_at: string;
  };
}

/** Preserve decimal input exactly; authoritative limits remain server-side. */
export function isPositiveMoney(value: string): boolean {
  const amount = String(value).trim();
  return /^\d+(?:\.\d+)?$/.test(amount) && /[1-9]/.test(amount);
}

export interface DepositRequest {
  amount: string;
  wallet_id: string;
  idempotency_key?: string;
  reference: string;
  reference_id: string;
  user_id: string;
}

export interface WithdrawRequest {
  amount: string;
  reference: string;
  reference_id: string;
  user_id: string;
  wallet_id: string;
}

export interface TransferRequest {
  from_wallet_id: string;
  to_wallet_id: string;
  amount: string;
  reference: string;
  reference_id: string;
}

export interface CreateWalletRequest {
  user_id: string;
  wallet_type_ids: number[];
  currency_id: number;
}

export interface Currency {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
}

export interface TransactionResponse {
  message: string;
  amount: string;
}

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private walletApiUrl =
    environment.walletServiceUrl || 'http://localhost:8082';

  constructor(private http: HttpClient) { }

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  getUserWallets(userId: string): Observable<Wallet[]> {
    return this.http.get<Wallet[]>(`${this.walletApiUrl}/user/${userId}`, {
      headers: this.getHeaders(),
    });
  }

  getAllWallets(): Observable<Wallet[]> {
    return this.http.get<Wallet[]>(`${this.walletApiUrl}`, {
      headers: this.getHeaders(),
    });
  }

  createWallets(userId: string): Observable<Wallet[]> {
    return this.http.post<Wallet[]>(
      `${this.walletApiUrl}/`,
      { user_id: userId },
      { headers: this.getHeaders() },
    );
  }

  deposit(request: DepositRequest): Observable<TransactionResponse> {
    return this.http.post<TransactionResponse>(
      `${this.walletApiUrl}/deposit`,
      request,
      { headers: this.getHeaders() },
    );
  }

  withdraw(request: WithdrawRequest): Observable<TransactionResponse> {
    return this.http.post<TransactionResponse>(
      `${this.walletApiUrl}/withdrawal`,
      request,
      { headers: this.getHeaders() },
    );
  }

  transfer(request: TransferRequest): Observable<TransactionResponse> {
    return this.http.post<TransactionResponse>(
      `${this.walletApiUrl}/transfer`,
      request,
      { headers: this.getHeaders() },
    );
  }

  activateAllWallets(userId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.walletApiUrl}/user/${userId}/activate-all`,
      {},
      { headers: this.getHeaders() },
    );
  }

  deactivateAllWallets(userId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.walletApiUrl}/user/${userId}/deactivate-all`,
      {},
      { headers: this.getHeaders() },
    );
  }

  getCurrencies(): Observable<Currency[]> {
    return this.http.get<Currency[]>(`${this.walletApiUrl}/currencies`, { headers: this.getHeaders() });
  }

  getWalletTypes(): Observable<any> {
    return this.http.get<any>(`${this.walletApiUrl}/wallet-types`, {
      headers: this.getHeaders(),
    });
  }

  createWallet(request: CreateWalletRequest): Observable<Wallet[]> {
    return this.http.post<Wallet[]>(
      `${this.walletApiUrl}/`,
      request,
      { headers: this.getHeaders() },
    );
  }

  activateWallet(walletId: string): Observable<any> {
    return this.http.patch<any>(
      `${this.walletApiUrl}/${walletId}/activate`,
      {},
      { headers: this.getHeaders() },
    );
  }
}
